import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, ExternalLink, Github } from 'lucide-react';
import { personalInfo, projects } from '../mock';

const ProjectDetail = () => {
  const { id } = useParams();
  const project = projects.find(p => p.id === id);

  if (!project) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-neutral-950 via-neutral-900 to-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Project Not Found</h1>
          <Link to="/">
            <Button className="bg-gray-200 hover:bg-white text-black font-semibold">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-neutral-950/80 backdrop-blur-md border-b border-neutral-800 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-white hover:text-gray-300 transition-colors">
            {personalInfo.name.split(' ').map((word, i) => i === 0 ? word[0] : '').join('')}VT
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/projects" className="text-neutral-300 hover:text-white transition-colors">Projects</Link>
            <Link to="/about" className="text-neutral-300 hover:text-white transition-colors">About</Link>
            <Link to="/contact">
              <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white">
                Contact Me
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          <Link to="/" className="inline-flex items-center text-gray-400 hover:text-gray-300 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Projects
          </Link>

          {/* Project Header */}
          <div className="mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              {project.subtitle || project.title}
            </h1>
            <div className="flex flex-wrap gap-3 mb-6">
              {project.technologies.map((tech, index) => (
                <Badge key={index} className="bg-gray-700 text-white hover:bg-gray-600 px-4 py-1 text-sm">
                  {tech}
                </Badge>
              ))}
            </div>
          </div>

          {/* Project Images */}
          <div className="space-y-6 mb-12">
            {project.images.map((image, index) => (
              <Card key={index} className="bg-neutral-900 border-neutral-800 overflow-hidden">
                <img 
                  src={image} 
                  alt={`${project.title} screenshot ${index + 1}`}
                  className="w-full h-auto object-cover"
                />
              </Card>
            ))}
            
            {/* YouTube Trailer Embed */}
            {project.links.youtube && (
              <Card className="bg-neutral-900 border-neutral-800 overflow-hidden">
                <CardContent className="p-0">
                  <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                    <iframe
                      className="absolute top-0 left-0 w-full h-full"
                      src={project.links.youtube.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                      title={`${project.title} Trailer`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* About Section */}
          <Card className="bg-neutral-900 border-neutral-800 mb-8">
            <CardContent className="pt-8">
              <h2 className="text-3xl font-bold text-gray-300 mb-4">About</h2>
              <p className="text-neutral-300 text-lg leading-relaxed mb-6">
                {project.description}
              </p>
              
              {/* Links */}
              {(project.links.repo || project.links.itch) && (
                <div className="flex flex-wrap gap-4 mt-6">
                  {project.links.repo && (
                    <a href={project.links.repo} target="_blank" rel="noopener noreferrer">
                      <Button className="bg-gray-200 hover:bg-white text-black font-semibold">
                        <Github className="w-4 h-4 mr-2" />
                        View Repository
                      </Button>
                    </a>
                  )}
                  {project.links.itch && (
                    <a href={project.links.itch} target="_blank" rel="noopener noreferrer">
                      <Button className="bg-gray-200 hover:bg-white text-black font-semibold">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Play on itch.io
                      </Button>
                    </a>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Team Section (if applicable) */}
          {project.team && project.team.length > 0 && (
            <Card className="bg-neutral-900 border-neutral-800">
              <CardContent className="pt-8">
                <h2 className="text-3xl font-bold text-gray-300 mb-6">Team</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {project.team.map((member, index) => (
                    <div key={index} className="p-4 bg-neutral-800 rounded-lg">
                      <p className="text-white font-semibold">{member.name}</p>
                      <p className="text-neutral-400">{member.role}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-neutral-800">
        <div className="max-w-7xl mx-auto text-center text-neutral-400">
          <p>&copy; 2025 {personalInfo.name}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ProjectDetail;